import React, { useMemo, useContext, Suspense } from 'react';
import { Switch, Redirect, Route, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getObject } from '../util/mapObject';
import Loader from './Loader';
import RedirectLoader, { isRedirectPath } from './RedirectLoader';
import RedirectLoaderScreen from './RedirectLoaderScreen';
import { RouterContextProvider } from '../context/RouterContext';
import AuthLoadingScreen from './AuthLoadingScreen';
import { Trans } from '@lingui/react/macro';
import WorkpageShellSkeleton from './WorkpageShellSkeleton';

function RouteSwitch({ routes }) {
  const auth = useContext(AuthContext);
  const location = useLocation();

  const allowedRoutes = useMemo(() => Object.entries(routes).filter(entry => checkRequirements(auth, entry[1].requires)), [auth]);

  const defaultRoute = useMemo(() => allowedRoutes.filter(entry => entry[1].default).sort((a, b) => b[1].default - a[1].default)[0], [allowedRoutes]);

  if (!auth?.user) {
    const currentRoute = Object.entries(routes).find(([path]) => {
      const route = new RegExp(`^${path.replace(/:[^/]+/g, '[^/]+')}$`);
      return route.test(location.pathname);
    });

    if (currentRoute && requiresLogin(currentRoute[1].requires)) {
      const redirectUrl = location.pathname + location.search;
      const encodedRedirect = encodeURIComponent(redirectUrl);
      return <Redirect to={`/login?redirect=${encodedRedirect}`} />;
    }
  }

  const fallback = isRedirectPath(location.pathname) ? (
    <RedirectLoaderScreen>
      <RedirectLoader />
    </RedirectLoaderScreen>
  ) : !auth?.user ? (
    // Not signed in yet, so the only route this Suspense boundary can resolve to is the
    // login page (or another public route) — keep it visually continuous with the
    // AuthLoadingScreen the user was just looking at instead of a bare spinner.
    <AuthLoadingScreen message={<Trans>Loading…</Trans>} />
  ) : (
    <WorkpageShellSkeleton />
  );

  return (
    <Suspense fallback={fallback}>
      <Switch>
        {allowedRoutes.map(([path, { ...route }], idx) => {
          const Component = route.component;
          return (
            <Route
              key={idx}
              path={path}
              {...route}
              render={routeProps => (
                <RouterContextProvider>
                  <Component {...routeProps} />
                </RouterContextProvider>
              )}
              component={undefined}
            />
          );
        })}
        {defaultRoute ? <Redirect to={defaultRoute[0]} /> : null}
      </Switch>
    </Suspense>
  );
}

// `requires` can be 'login', a dot-path into `user` checked for truthiness, a predicate
// function called with `user`, or an array combining any of the above (all must pass).
function checkRequirements(auth, requires) {
  const { user } = auth ?? {};
  if (Array.isArray(requires)) {
    return requires.every(requirement => checkRequirements({ user }, requirement));
  } else if (requires === 'login') {
    return !!user;
  } else if (typeof requires === 'function') {
    return !!requires(user);
  } else if (typeof requires === 'string') {
    return !!getObject(user, requires);
  }

  return true;
}

function requiresLogin(requires) {
  return requires === 'login' || (Array.isArray(requires) && requires.includes('login'));
}

export default RouteSwitch;
